import type { CSSProperties, FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'

const CONVERGING = [
    'M-40 170C320 170 430 470 720 520',
    'M-40 330C300 330 470 500 720 520',
    'M-40 630C280 630 450 545 720 520',
    'M-40 790C340 770 490 565 720 520',
    'M1480 210C1120 210 1020 480 720 520',
    'M1480 390C1160 390 990 510 720 520',
    'M1480 670C1140 670 1000 548 720 520',
    'M1480 830C1100 810 960 570 720 520'
]

const WAVES = [
    'M-40 240C360 160 980 360 1480 250',
    'M-40 380C420 300 1000 500 1480 380',
    'M-40 520C380 460 1040 620 1480 500',
    'M-40 660C440 600 960 760 1480 640'
]

interface FlowLinesProps {
    tone?: 'dark' | 'light'
    variant?: 'converge' | 'waves'
    className?: string
}

/**
 * Decorative financial flow lines with small data packets travelling
 * along them. Purely visual, hidden from assistive technology.
 */
export const FlowLines: FC<FlowLinesProps> = ({
    tone = 'dark',
    variant = 'converge',
    className
}): ReactNode => {
    const paths = variant === 'converge' ? CONVERGING : WAVES
    const line = tone === 'dark' ? 'rgb(57 26 24 / 0.11)' : 'rgb(233 230 216 / 0.09)'
    const packet = tone === 'dark' ? 'rgb(158 129 85 / 0.85)' : 'rgb(200 174 130 / 0.7)'

    return (
        <svg
            aria-hidden='true'
            focusable='false'
            viewBox='0 0 1440 900'
            preserveAspectRatio='xMidYMid slice'
            className={cn('pointer-events-none absolute inset-0 size-full', className)}
        >
            <g fill='none' strokeWidth='1' strokeLinecap='round'>
                {paths.map(d => (
                    <path key={d} d={d} stroke={line} vectorEffect='non-scaling-stroke' />
                ))}

                {paths.map((d, index) => (
                    <path
                        key={`packet-${d}`}
                        d={d}
                        stroke={packet}
                        strokeWidth='2'
                        className='flow-packet'
                        style={{
                            animationDelay: `${index * -1.7}s`,
                            animationDuration: `${10 + (index % 3) * 2.5}s`
                        } as CSSProperties}
                    />
                ))}
            </g>
        </svg>
    )
}
